import React, { useState, useEffect } from 'react';
import { apiClient, useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BookOpen, 
  Plus, 
  Phone,
  MapPin,
  Search,
  Edit,
  Trash,
  FileText,
  Download,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const DirectoryPage = () => {
  const [doctors, setDoctors] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showReferralForm, setShowReferralForm] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const { isDoctor } = useAuth();

  const [form, setForm] = useState({
    name: '',
    specialty: '',
    clinic_name: '',
    location: '',
    contact_number: '',
    notes: '',
    appointment_rules: ''
  });

  const [referralForm, setReferralForm] = useState({
    patient_id: '',
    referral_doctor_id: '',
    reason: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [doctorsRes, referralsRes, patientsRes] = await Promise.all([
        apiClient.get('/referral-doctors'),
        apiClient.get('/referrals'),
        apiClient.get('/patients')
      ]);
      setDoctors(doctorsRes.data);
      setReferrals(referralsRes.data);
      setPatients(patientsRes.data);
    } catch (error) {
      toast.error('Failed to load directory');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.specialty || !form.location || !form.contact_number) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (editingDoctor) {
        await apiClient.put(`/referral-doctors/${editingDoctor.doctor_id}`, form);
        toast.success('Doctor updated');
      } else {
        await apiClient.post('/referral-doctors', form);
        toast.success('Doctor added');
      }
      setShowForm(false);
      setEditingDoctor(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save doctor');
    }
  };

  const handleCreateReferral = async () => {
    if (!referralForm.patient_id || !referralForm.referral_doctor_id || !referralForm.reason) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await apiClient.post('/referrals', referralForm);
      toast.success('Referral created');
      setShowReferralForm(false);
      setReferralForm({ patient_id: '', referral_doctor_id: '', reason: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create referral');
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      specialty: '',
      clinic_name: '',
      location: '',
      contact_number: '',
      notes: '',
      appointment_rules: ''
    });
  };

  const handleEdit = (doctor) => {
    setEditingDoctor(doctor);
    setForm({
      name: doctor.name,
      specialty: doctor.specialty,
      clinic_name: doctor.clinic_name || '',
      location: doctor.location,
      contact_number: doctor.contact_number,
      notes: doctor.notes || '',
      appointment_rules: doctor.appointment_rules || ''
    });
    setShowForm(true);
  };

  const handleDelete = async () => {
    try {
      await apiClient.delete(`/referral-doctors/${deleteId}`);
      toast.success('Doctor deleted');
      setDeleteId(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete doctor');
    }
  };

  const downloadReferralPdf = async (referralId) => {
    try {
      const response = await apiClient.get(`/referrals/${referralId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `referral_${referralId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Referral slip downloaded');
    } catch (error) {
      toast.error('Failed to download referral');
    }
  };

  const filteredDoctors = doctors.filter(doc => 
    doc.name.toLowerCase().includes(search.toLowerCase()) ||
    doc.specialty.toLowerCase().includes(search.toLowerCase()) ||
    doc.location.toLowerCase().includes(search.toLowerCase())
  );

  const specialties = [...new Set(doctors.map(d => d.specialty))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="directory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-primary" strokeWidth={1.5} />
            Referral Directory
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {doctors.length} doctors you trust
          </p>
        </div>
        <div className="flex gap-2">
          {isDoctor && (
            <Button 
              onClick={() => {
                setEditingDoctor(null);
                resetForm();
                setShowForm(true);
              }}
              className="rounded-full"
              data-testid="add-doctor-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Doctor
            </Button>
          )}
          <Button 
            variant="outline"
            onClick={() => setShowReferralForm(true)}
            className="rounded-full"
            data-testid="create-referral-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Create Referral
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="directory">
        <TabsList>
          <TabsTrigger value="directory">Directory ({doctors.length})</TabsTrigger>
          <TabsTrigger value="referrals">Referrals ({referrals.length})</TabsTrigger>
        </TabsList>

        {/* Directory Tab */}
        <TabsContent value="directory" className="space-y-4 mt-4">
          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, specialty, or location..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="doctor-search"
            />
          </div>

          {/* Specialty Chips */}
          {specialties.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {specialties.map(spec => (
                <Badge 
                  key={spec}
                  variant="outline"
                  className="cursor-pointer hover:bg-secondary"
                  onClick={() => setSearch(spec)}
                >
                  {spec}
                </Badge>
              ))}
            </div>
          )}

          {/* Doctors List */}
          {filteredDoctors.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No doctors found</p>
                {isDoctor && (
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowForm(true)}
                  >
                    Add your first trusted doctor
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {filteredDoctors.map((doctor, index) => (
                <Card 
                  key={doctor.doctor_id}
                  className="card-hover animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`doctor-card-${doctor.doctor_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">Dr. {doctor.name}</h3>
                          <Badge variant="secondary">{doctor.specialty}</Badge>
                        </div>
                        {doctor.clinic_name && (
                          <p className="text-sm text-muted-foreground mt-1">{doctor.clinic_name}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {doctor.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {doctor.contact_number}
                          </span>
                        </div>
                        {doctor.appointment_rules && (
                          <p className="text-xs text-muted-foreground mt-2 italic">
                            {doctor.appointment_rules}
                          </p>
                        )}
                      </div>
                      {isDoctor && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(doctor)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleteId(doctor.doctor_id)}>
                            <Trash className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Referrals Tab */}
        <TabsContent value="referrals" className="space-y-4 mt-4">
          {referrals.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No referrals yet</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setShowReferralForm(true)}
                >
                  Create your first referral
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {referrals.map((referral, index) => (
                <Card 
                  key={referral.referral_id}
                  className="animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{referral.patient_name}</span>
                          <span className="text-muted-foreground">→</span>
                          <span className="font-medium">Dr. {referral.doctor_name}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{referral.reason}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(referral.created_at).toLocaleDateString('en-PH', { 
                            year: 'numeric', month: 'long', day: 'numeric'
                          })}
                        </p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => downloadReferralPdf(referral.referral_id)}
                        data-testid={`download-referral-${referral.referral_id}`}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Doctor Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingDoctor ? 'Edit Doctor' : 'Add Doctor to Directory'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Full name"
                />
              </div>
              <div>
                <Label>Specialty *</Label>
                <Input
                  value={form.specialty}
                  onChange={(e) => setForm({ ...form, specialty: e.target.value })}
                  placeholder="e.g., Cardiologist"
                />
              </div>
            </div>
            <div>
              <Label>Clinic Name</Label>
              <Input
                value={form.clinic_name}
                onChange={(e) => setForm({ ...form, clinic_name: e.target.value })}
                placeholder="Clinic or hospital name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Location *</Label>
                <Input
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="City"
                />
              </div>
              <div>
                <Label>Contact Number *</Label>
                <Input
                  value={form.contact_number}
                  onChange={(e) => setForm({ ...form, contact_number: e.target.value })}
                  placeholder="+63 9XX XXX XXXX"
                />
              </div>
            </div>
            <div>
              <Label>Appointment Rules</Label>
              <Input
                value={form.appointment_rules}
                onChange={(e) => setForm({ ...form, appointment_rules: e.target.value })}
                placeholder="e.g., By appointment only, Walk-ins welcome"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Personal notes about this doctor..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-doctor-btn">
              {editingDoctor ? 'Save Changes' : 'Add Doctor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Referral Dialog */}
      <Dialog open={showReferralForm} onOpenChange={setShowReferralForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Referral</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Patient *</Label>
              <Select 
                value={referralForm.patient_id} 
                onValueChange={(v) => setReferralForm({ ...referralForm, patient_id: v })}
              >
                <SelectTrigger data-testid="referral-patient-select">
                  <SelectValue placeholder="Select patient" />
                </SelectTrigger>
                <SelectContent>
                  {patients.map(p => (
                    <SelectItem key={p.patient_id} value={p.patient_id}>
                      {p.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Refer to Doctor *</Label>
              <Select 
                value={referralForm.referral_doctor_id} 
                onValueChange={(v) => setReferralForm({ ...referralForm, referral_doctor_id: v })}
              >
                <SelectTrigger data-testid="referral-doctor-select">
                  <SelectValue placeholder="Select doctor" />
                </SelectTrigger>
                <SelectContent>
                  {doctors.map(d => (
                    <SelectItem key={d.doctor_id} value={d.doctor_id}>
                      Dr. {d.name} - {d.specialty}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason for Referral *</Label>
              <Textarea
                value={referralForm.reason}
                onChange={(e) => setReferralForm({ ...referralForm, reason: e.target.value })}
                placeholder="Describe the reason for this referral..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowReferralForm(false)}>Cancel</Button>
            <Button onClick={handleCreateReferral} data-testid="save-referral-btn">Create Referral</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Doctor?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove this doctor from your referral directory. Existing referrals will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DirectoryPage;
